import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Copy, Check, ExternalLink, AlertCircle, QrCode } from 'lucide-react'
import { fetchTemplate } from '../utils/api'
import { generatePrivacySafeLink } from '../utils/hashData'
import type { Template, FormData } from '../types'

export function LinkGeneratorPage() {
  const { templateId } = useParams<{ templateId: string }>()
  const navigate = useNavigate()

  const [template, setTemplate] = useState<Template | null>(null)
  const [formData, setFormData] = useState<FormData>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!templateId) return

    fetchTemplate(templateId)
      .then(setTemplate)
      .catch(() => setError('Erro ao carregar o modelo'))
      .finally(() => setLoading(false))
  }, [templateId])

  const handleInputChange = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
  }

  const generatedLink = useMemo(() => {
    if (!template) return ''
    const baseUrl = window.location.origin
    return generatePrivacySafeLink(baseUrl, template.id, formData)
  }, [template, formData])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      alert('Erro ao copiar o link')
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error || !template) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{error || 'Modelo não encontrado'}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 py-6 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gerar Link</h1>
            <p className="text-sm text-gray-500">{template.name}</p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
            <QrCode className="w-5 h-5" />
            Link com Privacidade
          </h3>
          <p className="text-sm text-blue-700">
            Os dados inseridos abaixo serão codificados no fragmento da URL (após o #).
            Isto significa que os dados pessoais <strong>nunca são enviados para o servidor</strong> e
            não aparecem nos logs de acesso.
          </p>
        </div>

        <div className="card p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">Pré-preencher Dados</h2>
          <p className="text-sm text-gray-500">
            Preencha os campos que deseja pré-preencher no formulário do cliente.
            Campos vazios serão deixados em branco para o cliente preencher.
          </p>

          {template.fields.map((field) => (
            <div key={field.name}>
              <label htmlFor={`gen-${field.name}`} className="label">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  id={`gen-${field.name}`}
                  value={formData[field.name] || ''}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  className="input min-h-[80px] resize-none"
                  placeholder="Opcional"
                />
              ) : field.type === 'date' ? (
                <input
                  type="date"
                  id={`gen-${field.name}`}
                  value={formData[field.name] || ''}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  className="input"
                />
              ) : (
                <input
                  type={field.type}
                  id={`gen-${field.name}`}
                  value={formData[field.name] || ''}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  className="input"
                  placeholder="Opcional"
                />
              )}
            </div>
          ))}
        </div>

        <div className="card p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">Link Gerado</h2>
          
          <div className="bg-gray-50 rounded-xl p-4 break-all">
            <code className="text-sm text-gray-700">{generatedLink}</code>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleCopy}
              className="btn-primary flex-1 gap-2"
            >
              {copied ? (
                <>
                  <Check className="w-5 h-5" />
                  Copiado!
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5" />
                  Copiar Link
                </>
              )}
            </button>
            <a
              href={generatedLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary gap-2"
            >
              <ExternalLink className="w-5 h-5" />
              Testar
            </a>
          </div>
        </div>

        <div className="text-center text-sm text-gray-500">
          <p>
            Envie este link ao utilizador por email, SMS ou outro canal seguro.
          </p>
        </div>
      </div>
    </div>
  )
}
